import {
  EarlyPostopDto,
  GenericDashboardDto,
  Grouped,
  Listed,
  PeropStatus,
  PeropAllDto,
  PostopAllDto,
  PostopChronicPainDto,
  PreopAllDto,
  PreopAnemicDto,
  PreopCardiacStressDto,
  PreopChronicPainDto,
  PreopLabTestingDto,
  PreopSleepApneaDto,
  PreopSmokerDto,
  PreopSurgeryPlanningDto,
  PreopTelehealthDto
} from '@/modules/surgicalEvent';
import { wrapError } from '../../error';
import { DashboardService } from './dashboard.service';
import { doctorBffApi } from '@/modules';

type ArgOf<T extends keyof typeof DashboardService.prototype> = Parameters<
  DashboardService[T]
>[0];

export const dashboardApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    allPreopConsultations: builder.query<
      Listed<PreopAllDto>,
      ArgOf<'fetchPreopConsultations'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchPreopConsultations(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    surgeryPlanningPreop: builder.query<
      Listed<PreopSurgeryPlanningDto>,
      ArgOf<'fetchSurgeryPlanningPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchSurgeryPlanningPreops(pageArg)
          .catch(wrapError),
      providesTags: (result, error, arg) =>
        result && arg.comments === 'NOT_BLANK'
          ? [{ type: 'Dashboard', id: 'NOT_BLANK_QUERY' }]
          : ['Dashboard']
    }),

    labTestingPreop: builder.query<
      Listed<PreopLabTestingDto>,
      ArgOf<'fetchLabTestingPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchLabTestingPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    cardiacStressTestPreop: builder.query<
      Listed<PreopCardiacStressDto>,
      ArgOf<'fetchCardiacStressTestPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchCardiacStressTestPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    telehealthPreop: builder.query<
      Listed<PreopTelehealthDto>,
      ArgOf<'fetchTelehealthPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchTelehealthPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    smokerPreop: builder.query<
      Listed<PreopSmokerDto>,
      ArgOf<'fetchSmokerPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchSmokerPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    sleepApneaPreop: builder.query<
      Listed<PreopSleepApneaDto>,
      ArgOf<'fetchOsaRiskPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchOsaRiskPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    anemicPreop: builder.query<
      Listed<PreopAnemicDto>,
      ArgOf<'fetchAnemicPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchAnemicPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    malnutritionPreop: builder.query<
      Listed<GenericDashboardDto>,
      ArgOf<'fetchMalnutritionPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchMalnutritionPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    chronicPainPreop: builder.query<
      Listed<PreopChronicPainDto>,
      ArgOf<'fetchChronicPainPreops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchChronicPainPreops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    allPerop: builder.query<
      Grouped<PeropAllDto, PeropStatus>,
      ArgOf<'fetchPerops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (params) =>
        DashboardService.getInstance().fetchPerops(params).catch(wrapError),
      providesTags: ['Dashboard', { type: 'Dashboard', id: 'WITH_STAFF_NOTES' }]
    }),

    allPostop: builder.query<Listed<PostopAllDto>, ArgOf<'fetchPostops'>>({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance().fetchPostops(pageArg).catch(wrapError),
      providesTags: ['Dashboard', { type: 'Dashboard', id: 'WITH_STAFF_NOTES' }]
    }),

    earlyPostop: builder.query<
      Listed<EarlyPostopDto>,
      ArgOf<'fetchEarlyPostops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchEarlyPostops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    }),

    chronicPainPostop: builder.query<
      Listed<PostopChronicPainDto>,
      ArgOf<'fetchChronicPainPostops'>
    >({
      keepUnusedDataFor: 60 * 5,
      queryFn: (pageArg) =>
        DashboardService.getInstance()
          .fetchChronicPainPostops(pageArg)
          .catch(wrapError),
      providesTags: ['Dashboard']
    })
  }),
  overrideExisting: false
});

export const {
  useAllPreopConsultationsQuery: useAllPreopConsultations,
  useLabTestingPreopQuery: useLabTestingPreop,
  useSurgeryPlanningPreopQuery: useSurgeryPlanningPreop,
  useCardiacStressTestPreopQuery: useCardiacStressTestPreop,
  useTelehealthPreopQuery: useTelehealthPreop,
  useSmokerPreopQuery: useSmokerPreop,
  useSleepApneaPreopQuery: useSleepApneaPreop,
  useAnemicPreopQuery: useAnemicPreop,
  useMalnutritionPreopQuery: useMalnutritionPreop,
  useChronicPainPreopQuery: useChronicPainPreop,
  useAllPeropQuery: useAllPerop,
  useAllPostopQuery: useAllPostop,
  useEarlyPostopQuery: useEarlyPostop,
  useChronicPainPostopQuery: useChronicPainPostop
} = dashboardApi;
